/**
 * Theme Style Provider
 * Applies global theme styles to child components
 */

import React, { useMemo } from 'react';

function ThemeStyleProvider({ styles = {}, children }) {
  const cssVariables = useMemo(() => {
    const vars = {};

    // Colors
    if (styles.colors) {
      if (styles.colors.primary) vars['--theme-primary'] = styles.colors.primary;
      if (styles.colors.secondary) vars['--theme-secondary'] = styles.colors.secondary;
      if (styles.colors.accent) vars['--theme-accent'] = styles.colors.accent;
      if (styles.colors.background) vars['--theme-background'] = styles.colors.background;
      if (styles.colors.text) vars['--theme-text'] = styles.colors.text;
      if (styles.colors.muted) vars['--theme-muted'] = styles.colors.muted;
    }

    // Theme-specific renderers use these to swap real merchant-controlled
    // values on the storefront appearance toggle rather than hard-coded dark
    // palettes. `darkColors` intentionally mirrors `colors`.
    if (styles.darkColors) {
      if (styles.darkColors.primary) vars['--theme-dark-primary'] = styles.darkColors.primary;
      if (styles.darkColors.secondary) vars['--theme-dark-secondary'] = styles.darkColors.secondary;
      if (styles.darkColors.accent) vars['--theme-dark-accent'] = styles.darkColors.accent;
      if (styles.darkColors.background) vars['--theme-dark-background'] = styles.darkColors.background;
      if (styles.darkColors.text) vars['--theme-dark-text'] = styles.darkColors.text;
      if (styles.darkColors.muted) vars['--theme-dark-muted'] = styles.darkColors.muted;
    }

    // Spacing
    if (styles.spacing) {
      if (styles.spacing.sectionPadding) vars['--ts-section-padding'] = styles.spacing.sectionPadding;
      if (styles.spacing.containerWidth) vars['--ts-container-width'] = styles.spacing.containerWidth;
    }

    // Fonts
    if (styles.fonts) {
      if (styles.fonts.heading) vars['--theme-font-heading'] = styles.fonts.heading;
      if (styles.fonts.body) vars['--theme-font-body'] = styles.fonts.body;
    }

    return vars;
  }, [styles]);

  const style = {
    ...cssVariables,
    fontFamily: styles.fonts?.body || 'inherit',
  };

  return (
    <div className="ts-theme-provider" style={style}>
      {children}
    </div>
  );
}

/**
 * Merge base theme settings with store overrides
 */
export function mergeStyles(baseSettings = {}, overrides = {}) {
  return {
    colors: {
      ...baseSettings.colors,
      ...overrides?.colors,
    },
    darkColors: {
      ...baseSettings.darkColors,
      ...overrides?.darkColors,
    },
    fonts: {
      ...baseSettings.fonts,
      ...overrides?.fonts,
    },
    spacing: {
      ...baseSettings.spacing,
      ...overrides?.spacing,
    },
    buttons: {
      ...baseSettings.buttons,
      ...overrides?.buttons,
    },
  };
}

export default ThemeStyleProvider;
